import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { howtoApi } from '../../api/client';
import type { HowToCategory, HowToGuide, HowToGuideStatus } from '../../api/modules/howto';
import { useAuth } from '../../hooks/useAuth';

export function useHowToList() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading, logout } = useAuth();

  const [guides, setGuides] = useState<HowToGuide[]>([]);
  const [categories, setCategories] = useState<HowToCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<HowToGuideStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [guidesRes, categoriesRes] = await Promise.all([
        howtoApi.listGuides(statusFilter === 'all' ? undefined : { status: statusFilter }),
        howtoApi.listCategories(),
      ]);
      setGuides(guidesRes.guides);
      setCategories(categoriesRes.categories);
    } catch (err: any) {
      console.error('Failed to load how-to guides:', err);
      setError(err?.message || 'Failed to load guides');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard');
      return;
    }
    if (isAuthenticated && isAdmin) {
      load();
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate, load]);

  const filteredGuides = useMemo(() => {
    if (categoryFilter === 'all') return guides;
    return guides.filter(g => g.categoryId === categoryFilter);
  }, [guides, categoryFilter]);

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('Delete this How To guide? This cannot be undone.')) {
      return;
    }
    try {
      setDeletingId(id);
      await howtoApi.deleteGuide(id);
      setGuides(prev => prev.filter(g => g.id !== id));
    } catch (err: any) {
      console.error('Failed to delete guide:', err);
      setError(err?.message || 'Failed to delete guide');
    } finally {
      setDeletingId(null);
    }
  }, []);

  return {
    authLoading,
    isAuthenticated,
    isAdmin,
    logout,
    guides: filteredGuides,
    categories,
    isLoading,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    deletingId,
    error,
    handleDelete,
    reload: load,
  };
}
